import { useState, useRef, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import Stories from "react-insta-stories"
import {
    ChevronLeft, ChevronRight, Star, MapPin, Calendar,
    TrendingUp, Sparkles, Music, Gamepad2, Theater, Trophy, Heart, Flame
} from "lucide-react"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { selectEventFilters } from "@/store/features/events/eventsSlice"
import { useLocation, Link, useNavigate } from "react-router-dom"
import qs from "qs"
import { useGetEventsQuery } from "@/store/features/events/eventsAPI"

const mediaUrl = import.meta.env.VITE_MEDIA_URL || '/';
const categories = [
    { id: "all", label: "All Events", icon: Sparkles },
    { id: "music", label: "Music", icon: Music },
    { id: "sports", label: "Sports", icon: Trophy },
    { id: "theater", label: "Theater", icon: Theater },
    { id: "gaming", label: "Gaming", icon: Gamepad2 }
]

const badgeStyles = {
    UNMISSABLE: { bg: "rgba(var(--theme-primary), 0.15)", color: "rgb(var(--theme-primary))" },
    TRENDING: { bg: "rgba(var(--theme-accent), 0.15)", color: "rgb(var(--theme-accent))" },
    HOT: { bg: "rgba(255, 87, 51, 0.15)", color: "#FF5733" },
    NEW: { bg: "rgba(76, 175, 80, 0.15)", color: "#4CAF50" },
    "SOLD OUT": { bg: "rgba(158, 158, 158, 0.15)", color: "#9e9e9e" }
}

const organizers = [
    { name: "BookMyShow", avatar: "https://logo.clearbit.com/bookmyshow.com", rating: 4.8 },
    { name: "Sunburn", avatar: "https://logo.clearbit.com/sunburn.in", rating: 4.7 },
    { name: "MIGS", avatar: "https://logo.clearbit.com/migs.com", rating: 4.6 },
    { name: "Prithvi Theatre", avatar: "https://logo.clearbit.com/prithvitheatre.org", rating: 4.9 }
]

const formatDateRange = (start?: string | null, end?: string | null) => {
    if (!start) return 'TBD';
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : null;

    const startFormatted = startDate.toLocaleDateString('en-IN', {
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
    });

    if (endDate && end !== start) {
        const endFormatted = endDate.toLocaleDateString('en-IN', {
            weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
        });
        return `${startFormatted} - ${endFormatted}`;
    }
    return startFormatted;
};

// Reusable Image Component with Fallback - Fixed TypeScript Types
const ImageWithFallback = ({
    src,
    alt,
    className,
    fallbackIcon: FallbackIcon = Calendar,
    onError,
    ...props
}: {
    src?: string | null;
    alt: string;
    className?: string;
    fallbackIcon?: any;
    onError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
    [key: string]: any;
}) => {
    const [imgError, setImgError] = useState(false);

    const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        setImgError(true);
        if (onError) onError(e);
    };

    if (!src || imgError) {
        return (
            <div className={`bg-gradient-to-br from-theme-primary to-theme-accent flex items-center justify-center ${className}`}>
                <FallbackIcon className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-muted-foreground/50" />
            </div>
        );
    }

    return (
        <img
            src={src}
            alt={alt}
            className={className}
            onError={handleError}
            loading="lazy"
            {...props}
        />
    );
};


// React Insta Stories Component with Navigation and Fallback
function EventStories({ events }) {
    const [currentStory, setCurrentStory] = useState(null)
    const navigate = useNavigate()

    const featuredEvents = events.filter(e => e.featured)

    // Create stories data for react-insta-stories
    const stories = featuredEvents.map(event => ({
        content: ({ action, story }) => {
            const imageUrl = event?.media?.thumbnail?.url
                ? `${mediaUrl}${event.media.thumbnail.url}`
                : null;

            return (
                <div className="relative w-full h-full bg-black">
                    <ImageWithFallback
                        src={imageUrl}
                        alt={event.title}
                        className="w-full h-full object-cover"
                        fallbackIcon={Calendar}
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/90" />

                    {/* Event Details */}
                    <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                        <div className="max-w-lg">
                            <h2 className="text-3xl font-bold mb-4">{event.title}</h2>

                            <div className="flex items-center gap-6 mb-6 text-sm">
                                <div className="flex items-center gap-2">
                                    <Calendar className="h-5 w-5" />
                                    <span>{formatDateRange(event?.start_date, event?.end_date)}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <MapPin className="h-5 w-5" />
                                    <span>{event.location[0].address}</span>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 mb-4">
                                <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                                <span className="text-lg font-semibold">{event.share_count}</span>
                                <span className="text-sm opacity-80">({event.view_count} views)</span>
                            </div>

                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm opacity-80 mb-1">Starting from</p>
                                    <p className="text-4xl font-bold">₹{event.base_price || 'FREE'}</p>
                                </div>
                                <Button
                                    size="lg"
                                    className="text-white font-bold shadow-2xl hover:scale-105 transition-transform"
                                    style={{
                                        background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        action('pause')
                                        setCurrentStory(null)
                                        navigate(`/event/${event.documentId}`)
                                    }}
                                >
                                    Book Now
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        duration: 5000,
        header: {
            heading: event.title.substring(0, 30) + '...',
            subheading: formatDateRange(event?.start_date, event?.end_date),
            profileImage: event?.media?.thumbnail?.url
                ? `${mediaUrl}${event.media.thumbnail.url}`
                : undefined
        }
    }))

    return (
        <div className="mb-1">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h3 className="text-2xl font-bold gradient-text flex items-center gap-2"
                        style={{
                            background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}
                    >
                        <Flame className="h-6 w-6" style={{ color: 'rgb(var(--theme-accent))' }} />
                        Featured Stories
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">Tap to explore promoted events</p>
                </div>
                <Badge
                    className="glass-card border px-3 py-1.5 font-semibold animate-pulse"
                    style={{
                        borderColor: "rgba(var(--theme-accent), 0.5)",
                        background: "linear-gradient(90deg, rgba(var(--theme-accent), 0.2), transparent)"
                    }}
                >
                    <Sparkles className="h-3 w-3 mr-1 inline" />
                    Promoted
                </Badge>
            </div>

            {/* Stories Avatars */}
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                {featuredEvents.map((event, index) => {
                    const avatarUrl = event?.media?.thumbnail?.url
                        ? `${mediaUrl}${event.media.thumbnail.url}`
                        : null;

                    return (
                        <button
                            key={event.id}
                            onClick={() => setCurrentStory(index)}
                            className="flex-shrink-0 group cursor-pointer"
                        >
                            <div className="relative">
                                {/* Gradient Ring */}
                                <div
                                    className="w-24 h-24 rounded-full p-[3px] group-hover:scale-105 transition-transform duration-300"
                                    style={{
                                        background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                                    }}
                                >
                                    <div className="w-full h-full rounded-full bg-background p-[3px] overflow-hidden">
                                        <ImageWithFallback
                                            src={avatarUrl}
                                            alt={event.title}
                                            className="w-full h-full rounded-full object-cover"
                                            fallbackIcon={Calendar}
                                        />
                                    </div>
                                </div>
                                {/* Hot Badge */}
                                {index === 0 && (
                                    <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center shadow-lg"
                                        style={{
                                            background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                                        }}
                                    >
                                        <Flame className="h-4 w-4 text-white" />
                                    </div>
                                )}
                            </div>
                            <p className="text-xs font-medium text-center mt-2 max-w-[96px] truncate">
                                {event.title.split(':')[0]}
                            </p>
                        </button>
                    );
                })}
            </div>

            {/* Stories Modal */}
            {currentStory !== null && (
                <div className="fixed inset-0 z-50 bg-black">
                    <div className="relative w-full h-full max-w-lg mx-auto">
                        <Stories
                            key={currentStory}
                            stories={stories}
                            defaultInterval={5000}
                            width="100%"
                            height="100%"
                            currentIndex={currentStory}
                            onAllStoriesEnd={() => setCurrentStory(null)}
                            onStoryEnd={(s, st) => console.log('story ended', s, st)}
                            onStoryStart={(s, st) => console.log('story started', s, st)}
                            storyContainerStyles={{
                                maxWidth: '500px',
                                margin: '0 auto'
                            }}
                            loop={false}
                            keyboardNavigation={true}
                            preventDefault={true}
                            storyStyles={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        />

                        {/* Close Button */}
                        <button
                            onClick={() => setCurrentStory(null)}
                            className="absolute top-4 right-4 z-[60] p-3 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm transition-colors"
                        >
                            <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

// Section: Upcoming Events with Navigation
function UpcomingEvents({ events }) {
    const isUpcoming = (eventDate) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const compareDate = new Date(eventDate);
        compareDate.setHours(0, 0, 0, 0);
        return compareDate >= today;
    };

    const upcomingEvents = events.filter(event => isUpcoming(event.start_date)).slice(0, 5);

    return (
        <section className="py-6">
            <div className="mb-6">
                <h3 className="text-2xl font-bold gradient-text"
                    style={{
                        background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                    }}
                >
                    Upcoming Events
                </h3>
                <p className="text-sm text-muted-foreground mt-1">Don't miss what's coming next</p>
            </div>
            <div className="carousel-container">
                <div className="flex gap-4 md:gap-6 overflow-x-auto pb-4 scrollbar-hide scroll-smooth snap-x snap-mandatory">
                    {upcomingEvents.map(event => (
                        <div key={event.documentId} className="snap-start">
                            <Link to={`/event/${event?.documentId}`} className="block w-full">
                                <EventCard event={event} isLiked={false} onLike={() => { }} />
                            </Link>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    )
}

// Section: Popular Categories with Navigation
function PopularCategories() {
    const navigate = useNavigate()

    return (
        <section className="py-12">
            <div className="text-center mb-8">
                <h3 className="text-2xl font-bold gradient-text"
                    style={{
                        background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                    }}
                >
                    Popular Categories
                </h3>
                <p className="text-sm text-muted-foreground mt-1">Browse by your interests</p>
            </div>
            <div className="flex gap-6 justify-center flex-wrap">
                {categories.slice(1).map(({ icon: Icon, label, id }) => (
                    <button
                        key={id}
                        onClick={() => navigate(`/events?category=${id}`)}
                        className="group flex flex-col items-center justify-center w-28 h-28 rounded-full glass-card border hover:shadow-2xl hover:scale-105 transition-all duration-300"
                        style={{
                            borderColor: `rgba(var(--theme-primary), 0.2)`,
                            background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                        }}
                    >
                        <Icon className="h-8 w-8 mb-2 text-white group-hover:scale-110 transition-transform" />
                        <span className="font-semibold text-xs text-white">{label}</span>
                    </button>
                ))}
            </div>
        </section>
    )
}

// Section: Popular Organizers with Navigation
function PopularOrganizers() {
    const navigate = useNavigate()

    return (
        <section className="py-12">
            <div className="mb-6">
                <h3 className="text-2xl font-bold gradient-text"
                    style={{
                        background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                    }}
                >
                    Popular Organizers
                </h3>
                <p className="text-sm text-muted-foreground mt-1">Trusted by millions worldwide</p>
            </div>
            <div className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide">
                {organizers.map(org => (
                    <div
                        key={org.name}
                        onClick={() => navigate(`/organizer/${org.name.toLowerCase().replace(/\s+/g, '-')}`)}
                        className="flex flex-col items-center min-w-[120px] p-4 glass-card border rounded-2xl hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer"
                        style={{ borderColor: `rgba(var(--theme-primary), 0.2)` }}
                    >
                        <img src={org.avatar} alt={org.name} className="w-16 h-16 rounded-full bg-white mb-3 shadow-lg ring-2 ring-purple-500/20" />
                        <span className="font-semibold text-sm text-center">{org.name}</span>
                        <div className="flex items-center gap-1 mt-2">
                            <Star className="h-4 w-4 fill-current" style={{ color: 'rgb(var(--theme-accent))' }} />
                            <span className="font-medium text-sm">{org.rating}</span>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    )
}

// Main Marketplace Component with Navigation
export function EventMarketplace() {
    const [selectedCategory, setSelectedCategory] = useState("all")
    const [likedEvents, setLikedEvents] = useState(new Set())

    const featuredScrollRef = useRef(null)
    const allEventsScrollRef = useRef(null)
    const filters = useAppSelector(selectEventFilters);
    const dispatch = useAppDispatch();
    const location = useLocation();

    const query = useMemo(() => {
        const queryConfig: any = {
            populate: {
                location: {
                    fields: ['name', 'address', 'city', 'state', 'country', 'postal_code']
                },
                media: {
                    populate: {
                        thumbnail: {
                            fields: ['url']
                        },
                    }
                }
            },
            pagination: {
                page: filters.page,
                pageSize: filters.limit,
            },
            filters: {}
        };

        const sortMap: Record<string, string> = {
            createdAt: 'createdAt:desc',
            start_date: 'start_date:asc',
            title: 'title:asc',
            view_count: 'view_count:desc',
        };
        queryConfig.sort = [sortMap[filters.sortBy] || 'createdAt:desc'];

        return qs.stringify(queryConfig, { encodeValuesOnly: true });
    }, [filters]);

    const { data } = useGetEventsQuery(query);
    const events = data?.data || [];

    const filteredEvents = selectedCategory === "all"
        ? events
        : events.filter(event => event.category === selectedCategory)
    const featuredEvents = events.filter(e => e.featured)

    const scroll = (scrollRef, direction) => {
        if (!scrollRef.current) return

        const container = scrollRef.current
        const scrollAmount = direction === "left" ? -1 : 1
        const currentScroll = container.scrollLeft
        const cardWidth = container.querySelector('.event-card')?.offsetWidth || 320
        const gap = 24

        container.scrollBy({
            left: scrollAmount * (cardWidth + gap),
            behavior: 'smooth'
        })
    }

    const toggleLike = (eventId, e) => {
        if (e) {
            e.preventDefault()
            e.stopPropagation()
        }
        setLikedEvents(prev => {
            const newSet = new Set(prev)
            if (newSet.has(eventId)) newSet.delete(eventId)
            else newSet.add(eventId)
            return newSet
        })
    }

    return (
        <section className="relative py-6 sm:py-6 overflow-hidden bg-gradient-to-br from-background via-background to-background">
            {/* Background Grid */}
            <div className="absolute inset-0 opacity-20 dark:opacity-10 pointer-events-none">
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage: `
              linear-gradient(90deg, rgba(var(--theme-primary), 0.03) 1px, transparent 1px),
              linear-gradient(rgba(var(--theme-primary), 0.03) 1px, transparent 1px)
            `,
                        backgroundSize: '80px 80px'
                    }}
                />
            </div>

            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                {/* Hero Section */}
                {/* <header className="text-center mb-16">
                    <h1
                        className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4"
                        style={{
                            background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}
                    >
                        Discover Your Next Experience
                    </h1>
                    <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
                        Explore, book and manage events with enterprise confidence
                    </p>
                </header> */}

                {/* React Insta Stories with Navigation */}
                <EventStories events={events} />

                {/* Upcoming Events with Navigation */}
                <UpcomingEvents events={events} />

                {/* Featured Events with Navigation */}
                <section className="mb-6">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2
                                className="text-2xl sm:text-3xl font-bold flex items-center gap-2"
                                style={{
                                    background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent'
                                }}
                            >
                                <TrendingUp className="h-6 w-6" style={{ color: 'rgb(var(--theme-primary))' }} />
                                Featured Events
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1">Hand-picked selections just for you</p>
                        </div>
                        <div className="hidden md:flex gap-2">
                            <button
                                onClick={() => scroll(featuredScrollRef, "left")}
                                className="p-2.5 rounded-xl glass-card border hover:shadow-lg hover:scale-105 transition-all"
                                style={{ borderColor: `rgba(var(--theme-primary), 0.2)` }}
                                aria-label="Scroll left"
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </button>
                            <button
                                onClick={() => scroll(featuredScrollRef, "right")}
                                className="p-2.5 rounded-xl glass-card border hover:shadow-lg hover:scale-105 transition-all"
                                style={{ borderColor: `rgba(var(--theme-primary), 0.2)` }}
                                aria-label="Scroll right"
                            >
                                <ChevronRight className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                    <div className="carousel-container">
                        <div
                            ref={featuredScrollRef}
                            className="carousel-track"
                        >
                            {featuredEvents.map(event => (
                                <div key={event.id} className="carousel-item">
                                    <Link to={`/event/${event?.documentId}`} className="block w-full">
                                        <EventCard
                                            event={event}
                                            isLiked={likedEvents.has(event.id)}
                                            onLike={(e) => toggleLike(event.id, e)}
                                        />
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* All Events with Navigation */}
                <section>
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h3
                                className="text-2xl sm:text-3xl font-bold"
                                style={{
                                    background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent'
                                }}
                            >
                                {selectedCategory === "all" ? "All Events" : categories.find(c => c.id === selectedCategory)?.label}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1">Explore all available events</p>
                        </div>
                        <div className="hidden md:flex gap-2">
                            <button
                                onClick={() => scroll(allEventsScrollRef, "left")}
                                className="p-2.5 rounded-xl glass-card border hover:shadow-lg hover:scale-105 transition-all"
                                style={{ borderColor: `rgba(var(--theme-primary), 0.2)` }}
                                aria-label="Scroll left"
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </button>
                            <button
                                onClick={() => scroll(allEventsScrollRef, "right")}
                                className="p-2.5 rounded-xl glass-card border hover:shadow-lg hover:scale-105 transition-all"
                                style={{ borderColor: `rgba(var(--theme-primary), 0.2)` }}
                                aria-label="Scroll right"
                            >
                                <ChevronRight className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                    <div className="carousel-container">
                        <div
                            ref={allEventsScrollRef}
                            className="carousel-track"
                        >
                            {filteredEvents.map(event => (
                                <div key={event.id} className="carousel-item">
                                    <Link to={`/event/${event?.documentId}`} className="block w-full">
                                        <EventCard
                                            event={event}
                                            isLiked={likedEvents.has(event.id)}
                                            onLike={(e) => toggleLike(event.id, e)}
                                        />
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            </div>

            {/* Enhanced Custom Styles */}
            <style>{`
                .scrollbar-hide::-webkit-scrollbar { 
                    display: none; 
                }
                .scrollbar-hide { 
                    -ms-overflow-style: none; 
                    scrollbar-width: none; 
                }
                
                .carousel-container {
                    position: relative;
                    width: 100%;
                    overflow: hidden;
                }
                
                .carousel-track {
                    display: flex;
                    gap: 1rem;
                    overflow-x: auto;
                    overflow-y: hidden;
                    scroll-behavior: smooth;
                    scroll-snap-type: x mandatory;
                    scroll-padding: 0 1rem;
                    -webkit-overflow-scrolling: touch;
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                    padding-bottom: 1rem;
                }
                
                .carousel-track::-webkit-scrollbar {
                    display: none;
                }
                
                .carousel-item {
                    scroll-snap-align: start;
                    scroll-snap-stop: always;
                    flex-shrink: 0;
                }
                
                @media (max-width: 640px) {
                    .carousel-track {
                        gap: 0.75rem;
                        scroll-padding: 0 0.75rem;
                    }
                }
                
                @media (min-width: 768px) {
                    .carousel-track {
                        gap: 1.5rem;
                        scroll-padding: 0 1.5rem;
                    }
                }
            `}</style>
        </section>
    )
}

// Enhanced Event Card with Fallback Image
function EventCard({ event, isLiked, onLike }) {
    const imageUrl = event?.media?.thumbnail?.url
        ? `${mediaUrl}${event.media.thumbnail.url}`
        : null;

    return (
        <div className="event-card group flex-shrink-0 w-[240px] sm:w-[280px] md:w-[300px] lg:w-[320px]">
            <div className="relative overflow-hidden rounded-2xl mb-4 shadow-lg hover:shadow-2xl transition-shadow duration-300">
                <div className="relative aspect-[2/3]">
                    <ImageWithFallback
                        src={imageUrl}
                        alt={event.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        fallbackIcon={Calendar}
                    />
                    <div
                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        style={{ background: `linear-gradient(to top, rgba(0,0,0,0.9) 0%, transparent 60%)` }}
                    />

                    {event.badge && (
                        <div
                            className="absolute top-2 left-2 md:top-3 md:left-3 px-2 py-1 md:px-3 md:py-1.5 rounded-full text-[10px] md:text-xs font-bold backdrop-blur-md shadow-lg"
                            style={{
                                background: badgeStyles[event.badge]?.bg,
                                color: badgeStyles[event.badge]?.color
                            }}
                        >
                            {event.badge}
                        </div>
                    )}

                    <button
                        onClick={(e) => onLike(e)}
                        className="absolute top-2 right-2 md:top-3 md:right-3 p-2 md:p-2.5 rounded-full glass-card backdrop-blur-md transition-all duration-300 hover:scale-110 shadow-lg z-10"
                        style={{
                            background: isLiked ? `rgba(var(--theme-primary), 0.4)` : `rgba(255,255,255,0.25)`
                        }}
                    >
                        <Heart
                            className={`h-4 w-4 md:h-5 md:w-5 transition-colors ${isLiked ? 'fill-current' : ''}`}
                            style={{ color: isLiked ? `rgb(var(--theme-accent))` : 'white' }}
                        />
                    </button>

                    {event?.languages?.length > 0 && (
                        <div className="absolute bottom-2 left-2 md:bottom-3 md:left-3 flex gap-1.5 md:gap-2">
                            {event?.languages?.map(lang => (
                                <span
                                    key={lang}
                                    className="px-2 py-0.5 md:px-2.5 md:py-1 rounded-lg text-[10px] md:text-xs font-semibold glass-card backdrop-blur-md text-white shadow-md"
                                    style={{ background: `rgba(0,0,0,0.6)` }}
                                >
                                    {lang}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                <div
                    className="absolute -inset-1 rounded-2xl opacity-0 group-hover:opacity-40 blur-2xl transition-opacity duration-500 -z-10"
                    style={{ background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))` }}
                />
            </div>

            <div className="space-y-2 md:space-y-3 px-1">
                <h4
                    className="font-bold text-sm md:text-base lg:text-lg leading-tight line-clamp-2 transition-all duration-300"
                    style={{
                        background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                    }}
                >
                    {event.title}
                </h4>

                <div className="flex items-center gap-1.5 md:gap-2">
                    <Star className="h-3 w-3 md:h-4 md:w-4 fill-current flex-shrink-0" style={{ color: 'rgb(var(--theme-accent))' }} />
                    <span className="font-bold text-xs md:text-sm">{event.share_count}</span>
                    <span className="text-[10px] md:text-xs text-muted-foreground">({event.view_count} views)</span>
                </div>

                <div className="space-y-1.5 md:space-y-2 text-xs md:text-sm text-muted-foreground">
                    <div className="flex items-start gap-1.5 md:gap-2">
                        <MapPin className="h-3 w-3 md:h-4 md:w-4 flex-shrink-0 mt-0.5" />
                        <span className="line-clamp-1">{event.location[0].address}</span>
                    </div>
                    <div className="flex items-center gap-1.5 md:gap-2">
                        <Calendar className="h-3 w-3 md:h-4 md:w-4 flex-shrink-0" />
                        <span>{formatDateRange(event?.start_date, event?.end_date)}</span>
                    </div>
                </div>

                <div className="pt-2 md:pt-3 flex items-center justify-between border-t border-border/50">
                    <span className="text-[10px] md:text-xs text-muted-foreground">From</span>
                    <span
                        className="text-lg md:text-xl lg:text-2xl font-bold"
                        style={{
                            background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}
                    >
                        {event.base_price === 0 || event.base_price === null ? 'FREE' : `₹${event.base_price}`}
                    </span>
                </div>
            </div>
        </div>
    )
}
